import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  accountsApi,
  useFindExternalDataForAccountsMutation,
} from "../../../../api";
import { JobTitle } from "../../../../types/apiTypes";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { JobTitlesSortableList } from "../../../features/JobTitlesSortableList";
import { CustomToast } from "../../CustomToast";
import { ReuseModal } from "../../ReuseModal";
import { JobTitlesListHeader } from "../GetMoreContactsModal/JobTitleListHeader";
import { useAppDispatch } from "../../../../hooks";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initTitles: JobTitle[];
  campaign_id: string;
  onSuccess: () => void;
  selectedAccounts: string[];
};

export const EditJobTitlesModal = ({
  isOpen,
  setIsOpen,
  initTitles,
  campaign_id,
  onSuccess,
  selectedAccounts,
}: Props) => {
  const dispatch = useAppDispatch();
  const [titles, setTitles] = useState<JobTitle[]>(initTitles);
  const [createdTitleId, setCreatedTitleId] = useState<string>("");
  const [searchTitleId, setSearchTitleId] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchTitles, setSearchTitles] = useState<string[]>([]);

  const [findExternalDataForAccounts, { isLoading, error, isError }] =
    useFindExternalDataForAccountsMutation();

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to find external data for accounts."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
    }
  }, [isError, error]);

  useEffect(() => {
    setTitles(initTitles);
  }, [initTitles]);

  useEffect(() => {
    const searchTitleList = titles
      .filter(({ title }) =>
        title.toLowerCase().includes(searchValue.toLowerCase()),
      )
      .map(({ id }) => id);
    setSearchTitles(searchTitleList);
    setSearchTitleId(searchValue ? searchTitleList[0] : "");
  }, [searchValue, titles]);

  const addTitleHandler = (title: string, successHandler?: () => void) => {
    const id = Date.now().toString();
    if (
      titles
        .filter((el) => el.id !== id)
        .some(
          (el) => el.title.trim().toLowerCase() === title.trim().toLowerCase(),
        )
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Job Title Update Failed"
          text="Each job title must be unique. Please choose a different title."
        />,
      );
      return;
    }

    setTitles([...titles, { id, title, is_critical: true, is_edit: false }]);
    setCreatedTitleId(id);

    successHandler && successHandler();
  };

  const submitHandler = () => {
    if (titles.some(({ is_edit }) => is_edit)) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Please complete editing before proceeding."
        />,
      );
      return;
    }

    findExternalDataForAccounts({
      account_ids: selectedAccounts,
      campaign_id,
      title_gaps: titles.map(({ title, is_critical }) => ({
        name: title,
        is_critical,
      })),
    }).then(() => {
      onSuccess();
      setIsOpen(false);
      dispatch(accountsApi.util.invalidateTags(["list-view"]));
    });
  };

  const isNoTitles = titles.length === 0;

  const setSearchElIndexHandler = (index: number) => {
    setSearchTitleId(searchTitles[index]);
  };
  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Explore INFUSE Audience Expansion"
      maxWidth="548px"
      submitBtnText="Expand Audience"
      cancelBtnText="Back"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      isLoading={isLoading}
      isDisabled={isLoading || titles.length === 0}
    >
      <Stack gap="16px">
        <Typography component="span" variant="body1" color="text.secondary">
          Customize the list of titles for audience expansion.
        </Typography>
        <JobTitlesListHeader
          addTitleHandler={addTitleHandler}
          setSearchValue={setSearchValue}
          searchTotalCount={searchTitles.length}
          searchElIndex={searchTitles.findIndex((el) => searchTitleId === el)}
          setSearchElIndex={setSearchElIndexHandler}
          isLimit={titles.length >= 100}
        />
        {!isNoTitles && (
          <JobTitlesSortableList
            titles={titles}
            setTitles={setTitles}
            createdTitleId={createdTitleId}
            setCreatedTitleId={setCreatedTitleId}
            searchTitleId={searchTitleId}
          />
        )}
      </Stack>
    </ReuseModal>
  );
};
